/*----------------------------------
- TOOLS MENU CATALOG (SSOT)
-----------------------------------*/

import type { IconName } from '@uniquedomains/components/ui/icon';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';

/*----------------------------------
- TYPES
-----------------------------------*/

/**
 * A header Tools row. A row either links somewhere (`href`) or announces itself as
 * unbuilt (`status`), never both: a shipped tool must not still read "Coming soon",
 * and an unbuilt one must never be a dead link.
 *
 * `cta` is the short action note on the right of a desktop row. Only a shipped tool
 * carries one, because only a shipped tool has an action to name.
 */
export type LandingToolsMenuItem = {
    id: string;
    label: string;
    description: string;
    iconName: IconName;
    cta?: string;
} & (
    | { href: string; status?: undefined }
    | { href?: undefined; status: 'Coming soon' }
);

/*----------------------------------
- CATALOG
-----------------------------------*/

export const LANDING_TOOLS_MENU_ITEMS: readonly LandingToolsMenuItem[] = [
    {
        id: 'multi-appraisal',
        label: 'Multi-Appraisal',
        description: 'Run one name or a list through several appraisers and read them on one axis.',
        iconName: 'chart-bar',
        cta: 'Appraise a name',
        href: LANDING_WEBSITE_PAGE_ROUTES.productAppraise,
    },
    // Shipped 2026-10-09 (ADR-0078): one live lookup a day each for a visitor.
    {
        id: 'whois-rdap',
        label: 'WHOIS / RDAP checker',
        description: 'Who holds a domain, when it was registered and when it expires.',
        iconName: 'file-text',
        cta: 'Look up a name',
        href: LANDING_WEBSITE_PAGE_ROUTES.whois,
    },
    {
        id: 'availability-check',
        label: 'Availability checker',
        description: 'Is this name free to register, and on which endings?',
        iconName: 'check',
        cta: 'Check a name',
        href: LANDING_WEBSITE_PAGE_ROUTES.availability,
    },
    {
        id: 'brand-logo-generator',
        label: 'Brand logo generator',
        description: 'A first logo for a name you are considering, to see it as a brand.',
        iconName: 'pencil',
        status: 'Coming soon',
    },
] as const;

/**
 * The pages the Tools trigger lights up on. Derived from the catalog so a new tool
 * cannot ship with a menu row and a dead highlight; the unbuilt rows have no page.
 */
export const LANDING_TOOLS_MENU_ACTIVE_PATH_PREFIXES: readonly string[] = LANDING_TOOLS_MENU_ITEMS.reduce<string[]>(
    (paths, item) => (item.href ? [...paths, item.href] : paths),
    [],
);
